"use client";

import type { FC } from "react";
import { Suspense } from "react";

import { isEditing, isPreviewing } from "@builder.io/sdk-react";
import type { LinkProps } from "next/link";
import NextLink from "next/link";
import urlJoin from "url-join";

import { useLocale } from "@/context/LocaleContext";
import { isDefinedLocale } from "@/libs/i18n/tools/isDefinedLocale";

import { i18n } from "../i18n";

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const getLocalizedHref = (href: LinkProps["href"], localizedPathname: string) => {
  if (typeof href === "string") {
    return localizedPathname;
  }
  return { ...href, pathname: localizedPathname };
};

export const LinkContent: FC<
  LinkProps & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps
> = ({ href, ...rest }) => {
  const { currentLocale = i18n.defaultLocale } = useLocale();
  const isPrefetchEnabled = !isEditing() && !isPreviewing();

  const pathname = typeof href === "string" ? href : href.pathname || "";
  const localeSlug = pathname.split("/").at(1);
  const isLocaleSlugMissing = !localeSlug || !isDefinedLocale(localeSlug);
  const isPathnameValidUrl = isValidUrl(pathname); // skip localization if the pathname is a valid URL starting with a protocol (http://, https://, mailto:)
  const needsToBeLocalized = !isPathnameValidUrl && isLocaleSlugMissing;

  const localizedPathname = urlJoin("/", currentLocale, pathname);
  const localizedHref = needsToBeLocalized ? getLocalizedHref(href, localizedPathname) : href;

  return <NextLink href={localizedHref} prefetch={isPrefetchEnabled} {...rest} />;
};

export const Link: FC<LinkProps & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & LinkProps> = ({
  href,
  children,
  ...rest
}) => (
  <Suspense
    fallback={
      <a href={typeof href === "string" ? href : (href?.pathname ?? "")} {...rest}>
        {children}
      </a>
    }
  >
    <LinkContent href={href} {...rest}>
      {children}
    </LinkContent>
  </Suspense>
);
